import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './PostCard.css';

const PostCard = ({ post, disableHorizontalSwipe = false, onClick, isActive = false, autoPlayVideo = false, onEdit, onDelete }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [swipeDirection, setSwipeDirection] = useState(null); // 'horizontal' or 'vertical'
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasDragged, setHasDragged] = useState(false); // 실제로 드래그가 발생했는지 추적
  const [clickTarget, setClickTarget] = useState(null); // 클릭된 요소 저장
  const [showMenu, setShowMenu] = useState(false); // 메뉴 표시 여부
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const lastToggleTime = useRef(0); // 마지막 토글 시간 (중복 방지)
  const menuRef = useRef(null); // 메뉴 참조
  const navigate = useNavigate();
  const { user } = useAuth();

  const media = post.media || [];
  
  // 비디오 확인 (비디오는 단일만 허용)
  const hasVideo = media.some(m => m.mediaType === 'VIDEO');
  
  // 이미지가 여러 개면 버튼 표시 (비디오는 1개만 허용)
  const hasMultipleMedia = !hasVideo && media.length > 1;

  // 최소 스와이프 거리 (픽셀)
  const minSwipeDistance = 50;

  // 권한 체크: 본인 게시글이거나 관리자인지
  const canManagePost = user && (user.userId === post.user.userId || user.role === 'ADMIN');

  // 메뉴 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showMenu]);

  // 릴스 자동재생 제어 (isActive 기반)
  useEffect(() => {
    const currentMedia = media[currentIndex];
    
    if (currentMedia?.mediaType === 'VIDEO' && autoPlayVideo) {
      if (isActive && videoRef.current) {
        // 현재 보이는 릴스면 재생
        videoRef.current.currentTime = 0; // 처음부터 재생
        videoRef.current.play().then(() => {
          setIsPlaying(true);
        }).catch(err => {
          console.log('Video autoplay failed:', err);
          setIsPlaying(false);
        });
      }
    }

    // cleanup: 비활성화되거나 다른 슬라이드로 이동하면 정지
    return () => {
      if (videoRef.current && autoPlayVideo) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
        setIsPlaying(false);
      }
    };
  }, [isActive, currentIndex, autoPlayVideo, media]);

  // 피드에서 비디오 자동재생 제어 (Intersection Observer 기반)
  useEffect(() => {
    const currentMedia = media[currentIndex];
    
    // 피드 모드(!autoPlayVideo)이고 비디오일 때만
    if (currentMedia?.mediaType === 'VIDEO' && !autoPlayVideo && videoRef.current) {
      const video = videoRef.current;
      
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // 화면에 들어오면 자동재생 (음소거 상태)
              video.muted = true;
              video.play().then(() => {
                setIsPlaying(true);
              }).catch(err => {
                console.log('Video autoplay failed:', err);
              });
            } else {
              // 화면 밖으로 나가면 정지 및 리셋
              video.pause();
              video.currentTime = 0;
              setIsPlaying(false);
            }
          });
        },
        { threshold: 0.5 } // 50% 이상 보이면 재생
      );

      observer.observe(video);

      return () => {
        observer.disconnect();
        video.pause();
        video.currentTime = 0;
      };
    }
  }, [currentIndex, autoPlayVideo, media]);

  // 음소거 토글
  const toggleMute = () => {
    if (videoRef.current) {
      setIsMuted(!isMuted);
      videoRef.current.muted = !isMuted;
    }
  };

  // 메뉴 토글
  const toggleMenu = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  // 수정 버튼 클릭
  const handleEdit = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    if (onEdit) {
      onEdit(post);
    }
  };

  // 삭제 버튼 클릭
  const handleDelete = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    if (onDelete) {
      onDelete(post);
    }
  };

  // 재생/정지 토글 (릴스에서만 사용)
  const togglePlayPause = () => {
    if (!videoRef.current || !autoPlayVideo) return;
    
    // 중복 클릭 방지 (300ms 내 중복 호출 무시)
    const now = Date.now();
    if (now - lastToggleTime.current < 300) {
      return;
    }
    lastToggleTime.current = now;
    
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(err => {
        console.log('Video play failed:', err);
      });
    }
  };

  if (media.length === 0) return null;

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < media.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  // 마우스/터치 시작
  const handleDragStart = (clientX, clientY, target) => {
    setIsDragging(true);
    setStartX(clientX);
    setStartY(clientY);
    setDragOffset(0);
    setSwipeDirection(null); // 방향 초기화
    setHasDragged(false); // 드래그 플래그 초기화
    setClickTarget(target); // 클릭된 요소 저장
  };

  // 마우스/터치 이동
  const handleDragMove = (clientX, clientY) => {
    if (!isDragging) return;

    const diffX = clientX - startX;
    const diffY = clientY - startY;

    // 10px 이상 움직였으면 드래그로 간주
    if (Math.abs(diffX) > 10 || Math.abs(diffY) > 10) {
      setHasDragged(true);
    }

    // 첫 이동에서 방향 결정 (한번 결정되면 유지)
    if (!swipeDirection && (Math.abs(diffX) > 10 || Math.abs(diffY) > 10)) {
      if (Math.abs(diffX) > Math.abs(diffY)) {
        // disableHorizontalSwipe가 true면 가로 스와이프 방향 설정 안 함
        if (!disableHorizontalSwipe) {
          setSwipeDirection('horizontal');
        }
      } else {
        setSwipeDirection('vertical');
      }
    }

    // 가로 스와이프만 처리 (세로는 무시 - 릴스 스크롤)
    // disableHorizontalSwipe가 true면 아예 움직이지 않음
    if (swipeDirection === 'horizontal' && !disableHorizontalSwipe) {
      const containerWidth = containerRef.current?.offsetWidth || 0;
      
      // 첫 번째 사진에서 오른쪽으로, 마지막 사진에서 왼쪽으로 드래그 시 저항 추가
      let resistance = 1;
      if ((currentIndex === 0 && diffX > 0) || (currentIndex === media.length - 1 && diffX < 0)) {
        resistance = 0.3; // 30%만 움직임
      }

      setDragOffset(diffX * resistance);
    }
  };

  // 마우스/터치 종료
  const handleDragEnd = () => {
    if (!isDragging) return;

    // 가로 스와이프였을 경우만 페이지 전환
    if (swipeDirection === 'horizontal' && !disableHorizontalSwipe) {
      const containerWidth = containerRef.current?.offsetWidth || 0;
      const threshold = containerWidth * 0.2; // 20% 이상 드래그 시 페이지 전환

      if (Math.abs(dragOffset) > threshold) {
        if (dragOffset < 0 && currentIndex < media.length - 1) {
          // 왼쪽으로 스와이프 -> 다음
          setCurrentIndex(currentIndex + 1);
        } else if (dragOffset > 0 && currentIndex > 0) {
          // 오른쪽으로 스와이프 -> 이전
          setCurrentIndex(currentIndex - 1);
        }
      }
    }
    
    // 드래그가 발생하지 않은 단순 클릭인 경우
    if (!hasDragged) {
      // 릴스의 비디오 클릭 시 재생/정지 토글
      if (clickTarget?.tagName === 'VIDEO' && autoPlayVideo) {
        togglePlayPause();
      }
      // 그 외의 경우 릴스로 이동 (onClick이 있을 때)
      else if (onClick) {
        // 현재 슬라이드 인덱스도 함께 전달
        onClick(post, currentIndex);
      }
    }

    setIsDragging(false);
    setDragOffset(0);
    setSwipeDirection(null);
    setHasDragged(false);
    setClickTarget(null);
  };

  // 마우스 이벤트
  const onMouseDown = (e) => {
    // 버튼 클릭 시 드래그 방지
    if (e.target.tagName === 'BUTTON' ||
        e.target.closest('.carousel-btn') ||
        e.target.closest('.carousel-indicators') ||
        e.target.closest('.mute-btn')) {
      return;
    }
    
    handleDragStart(e.clientX, e.clientY, e.target);
  };

  const onMouseMove = (e) => {
    handleDragMove(e.clientX, e.clientY);
  };

  const onMouseUp = () => {
    handleDragEnd();
  };

  const onMouseLeave = () => {
    if (isDragging) {
      handleDragEnd();
    }
  };

  // 터치 이벤트
  const onTouchStart = (e) => {
    // 버튼 터치 시 드래그 방지
    if (e.target.tagName === 'BUTTON' ||
        e.target.closest('.carousel-btn') ||
        e.target.closest('.carousel-indicators') ||
        e.target.closest('.mute-btn')) {
      return;
    }
    
    handleDragStart(e.touches[0].clientX, e.touches[0].clientY, e.target);
  };

  const onTouchMove = (e) => {
    handleDragMove(e.touches[0].clientX, e.touches[0].clientY);
  };

  const onTouchEnd = (e) => {
    // 버튼 터치가 아닐 때만 preventDefault
    if (e.target.tagName !== 'BUTTON' && 
        !e.target.closest('.carousel-btn') && 
        !e.target.closest('.mute-btn')) {
      e.preventDefault(); // 마우스 이벤트 중복 방지
    }
    handleDragEnd();
  };

  // 현재 transform 계산
  const getTransform = () => {
    const containerWidth = containerRef.current?.offsetWidth || 0;
    const baseOffset = -currentIndex * containerWidth;
    return baseOffset + dragOffset;
  };

  return (
    <div className="post-card">
      <div className="post-header">
        <div className="user-info">
          {post.user.profileImageUrl && (
            <img 
              src={post.user.profileImageUrl} 
              alt={post.user.username}
              className="profile-image"
            />
          )}
          <span className="username">{post.user.username}</span>
        </div>
        <div className="header-right">
          <span className="post-date">
            {new Date(post.createdAt).toLocaleDateString('ko-KR')}
          </span>
          {canManagePost && (
            <div className="post-menu" ref={menuRef}>
              <button className="menu-btn" onClick={toggleMenu}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="5" r="1.5" />
                  <circle cx="12" cy="12" r="1.5" />
                  <circle cx="12" cy="19" r="1.5" />
                </svg>
              </button>
              {showMenu && (
                <div className="menu-dropdown">
                  <button className="menu-item" onClick={handleEdit}>수정</button>
                  <button className="menu-item delete" onClick={handleDelete}>삭제</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div 
        ref={containerRef}
        className="post-media-container"
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div 
          className={`post-media-slider ${isDragging ? 'dragging' : ''}`}
          style={{
            transform: `translateX(${getTransform()}px)`,
            transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.25, 0.1, 0.25, 1)'
          }}
        >
          {media.map((item, index) => (
            <div key={index} className="post-media-slide">
              {item.mediaType === 'IMAGE' ? (
                <img 
                  src={item.originalUrl} 
                  alt={`post ${index + 1}`} 
                  className="media-content"
                  draggable={false}
                />
              ) : (
                <div className={`video-container ${autoPlayVideo && !isPlaying ? 'paused' : ''}`}>
                  <video 
                    ref={index === currentIndex ? videoRef : null}
                    src={item.originalUrl} 
                    className="media-content"
                    loop
                    playsInline
                    muted={isMuted}
                    controls={!autoPlayVideo}
                  />
                  
                  {/* 재생/정지 오버레이 (릴스에서만) */}
                  {autoPlayVideo && (
                    <div className={`play-pause-overlay ${isDragging ? '' : 'show'}`} key={isPlaying ? 'playing' : 'paused'}>
                      {isPlaying ? (
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="white">
                          <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                        </svg>
                      ) : (
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="white">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      )}
                    </div>
                  )}
                  
                  {autoPlayVideo && (
                    <button 
                      className="mute-btn"
                      onClick={toggleMute}
                      aria-label={isMuted ? '소리 켜기' : '소리 끄기'}
                    >
                      {isMuted ? (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                          <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                        </svg>
                      ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                        </svg>
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* 좌우 버튼 (2개 이상일 때만) */}
        {hasMultipleMedia && (
          <>
            {currentIndex > 0 && (
              <button 
                className="carousel-btn carousel-btn-prev" 
                onClick={handlePrev}
                aria-label="이전 사진"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>
            )}
            {currentIndex < media.length - 1 && (
              <button 
                className="carousel-btn carousel-btn-next" 
                onClick={handleNext}
                aria-label="다음 사진"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            )}

            {/* 카운터 */}
            <div className="media-counter">
              {currentIndex + 1} / {media.length}
            </div>
          </>
        )}
      </div>

      {post.caption && (
        <div className="post-caption">
          <span className="caption-username">{post.user.username}</span>
          <span className="caption-text">{post.caption}</span>
          
          {/* 캡션이 있으면 해시태그를 캡션 안에 표시 */}
          {post.hashtags && post.hashtags.length > 0 && (
            <div className="post-hashtags">
              {post.hashtags.map((tag, index) => (
                <span 
                  key={index} 
                  className="hashtag"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/search?hashtag=${encodeURIComponent(tag)}`);
                  }}
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 캡션이 없을 때만 해시태그만 표시 */}
      {!post.caption && post.hashtags && post.hashtags.length > 0 && (
        <div className="post-hashtags">
          {post.hashtags.map((tag, index) => (
            <span 
              key={index} 
              className="hashtag"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/search?hashtag=${encodeURIComponent(tag)}`);
              }}
            >
              #{tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default PostCard;
